<template>
  <div
    class="product-container"
    :class="[shouldFavHover && !favorite && 'hover', spaceBottom && 'space-bottom', displayedProduct.isSkeleton && 'skeleton-product']"
  >
    <a
      ref="productLink"
      class="product-link"
      :href="displayedProduct.url"
      :target="openInNewTab ? '_blank' : '_self'"
      @click="click"
      :aria-label="product.title"
    >
      <div class="product-image-container product-maintain-aspect">
        <transition name="fade">
          <div v-if="displayedProduct && displayedProduct.productImage" :key="displayedProduct.productImage.key" class="product-image">
            <responsive-image
              :url="displayedProduct.productImage.url"
              :breakpoints="[0, 360, 500, 768]"
              :widths="[360, 500, 768, 450]"
              :skeleton="true"
              :alt="product.title"
            ></responsive-image>
          </div>
        </transition>
      </div>
      <div v-if="displayedProduct.title && showTitle" :class="[$slots.responsiveCartButton && 'hide-on-mobile']">
        <slot name="customTitle">
          <h2 class="product-title no-transform" :class="{ skeleton: displayedProduct.isSkeleton }">
            {{ displayedProduct.title }}
          </h2>
        </slot>
      </div>

      <transition name="fade">
        <suspense>
          <template v-if="badge">
            <ProductBadge v-if="displayedProduct.isOnSale" class="badge" :type="'sale'" />
            <ProductBadge v-else-if="displayedProduct.isOnClearance" class="badge" :type="'clearance'" />
          </template>
        </suspense>
      </transition>
    </a>

    <div class="product-detail" :class="[$slots.responsiveCartButton && 'hide-on-mobile']">
      <slot name="detail">
        <div class="prices">
          <div v-if="displayedProduct.isOnSale || displayedProduct.isOnClearance" class="originalPrice">{{ displayedProduct.originalPrice }}</div>
          <div class="newPrice" :class="{ skeleton: displayedProduct.isSkeleton }">{{ displayedProduct.price }}</div>
        </div>
        <slot name="responsiveCartButton"></slot>
        <slot v-if="!$slots.responsiveCartButton" name="fixedCartCutton">
          <div v-if="showVariations && variations && variations.length > 1" class="product-variations">
            <div
              v-for="variation in variations"
              :key="variation.id"
              class="product-variation"
              :class="{ active: displayedProduct.id === variation.id }"
              @mouseover="hover(variation)"
            >
              <a :href="variation.url" :target="openInNewTab ? '_blank' : '_self'">
                <responsive-image v-if="variation.swatchImage" :url="variation.swatchImage.url" :skeleton="true"></responsive-image>
              </a>
            </div>
          </div>
        </slot>
      </slot>
    </div>

    <div v-if="$slots.responsiveCartButton" class="product-detail-mobile">
      <div>
        <a v-if="displayedProduct.title && showTitle" :href="displayedProduct.url" :target="openInNewTab ? '_blank' : '_self'" @click="click">
          <slot name="customTitle">
            <h2 class="product-title no-transform" :class="{ skeleton: displayedProduct.isSkeleton }">{{ displayedProduct.title }}</h2>
          </slot>
        </a>
        <div class="prices">
          <div v-if="displayedProduct.isOnSale || displayedProduct.isOnClearance" class="originalPrice">{{ displayedProduct.originalPrice }}</div>
          <div class="newPrice" :class="{ skeleton: displayedProduct.isSkeleton }">{{ displayedProduct.price }}</div>
        </div>
      </div>
      <div class="slot-wrapper">
        <slot name="responsiveCartButton"></slot>
      </div>
    </div>

    <div class="favorite" @click="onFavoriteClicked($event)" ref="favoriteButton">
      <span v-if="favorite" class="af af-heart-full"></span>
      <span v-else class="af af-heart"></span>
    </div>
  </div>
</template>
<script>
import { isTouch } from '@/components/commons/utils.js';
import { Swipe } from '@/pixel/index';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';

import useFavorite from '@/compositions/useFavorite.js';
import { ref, watch, defineAsyncComponent } from 'vue';

export default {
  name: 'Product',

  components: {
    ResponsiveImage,
    ProductBadge: defineAsyncComponent(() => import('./ProductBadge.vue')),
  },

  props: {
    product: {
      type: Object,
      default: null,
    },
    badge: {
      type: Boolean,
      default: true,
    },
    showVariations: {
      type: Boolean,
      default: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    spaceBottom: {
      type: Boolean,
      default: true,
    },
    viewdFn: {
      type: Function,
      default: () => {},
    },
    openInNewTab: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const favorite = ref(false);
    const favoriteButton = ref(null);
    const selectedProduct = ref(
      props.product ?? {
        id: null,
        productImageKey: null,
      },
    );
    const { state: favoritesState, favorites, updateFavoriteData } = useFavorite();

    function updateFavorite() {
      if (selectedProduct.value) {
        favorite.value = selectedProduct.value.id in favorites.value;
      }
    }

    watch(
      () => ({ favoritesState }),
      () => {
        updateFavorite();
      },
      { deep: true, immediate: true },
    );

    watch(selectedProduct, () => {
      updateFavorite();
    });

    return {
      favorite,
      favorites,
      selectedProduct,
      updateFavoriteData,
      favoriteButton,
    };
  },

  data() {
    return {
      shouldFavHover: !isTouch(),
      imageError: false,
      imageLoaded: false,
    };
  },

  computed: {
    displayedProduct() {
      if (this.selectedProduct && this.selectedProduct.id) {
        return this.selectedProduct;
      }
      if (this.product && this.product.id) {
        return this.product;
      }
      return {
        id: 'skeleton',
        url: '',
        title: 'skeleton',
        price: 'skeleton',
        productImage: {
          key: '',
          url: '//about:blank?',
          alt: 'Loading..',
        },
        isSkeleton: true,
      };
    },

    variations() {
      if (this.displayedProduct.isSkeleton) {
        return [
          {
            id: 'skeleton-variation1',
            swatchImage: {
              key: '',
              url: '//about:blank?',
              alt: 'Loading..',
            },
            isSkeleton: true,
          },
          {
            id: 'skeleton-variation2',
            swatchImage: {
              key: '',
              url: '//about:blank?',
              alt: 'Loading..',
            },
            isSkeleton: true,
          },
          {
            id: 'skeleton-variation3',
            swatchImage: {
              key: '',
              url: '//about:blank?',
              alt: 'Loading..',
            },
            isSkeleton: true,
          },
          {
            id: 'skeleton-variation4',
            swatchImage: {
              key: '',
              url: '//about:blank?',
              alt: 'Loading..',
            },
            isSkeleton: true,
          },
        ];
      }
      if (this.product && (!this.product.variations || !this.product.variations.length)) {
        return [this.product];
      }
      return this.product && this.product.variations ? [this.product, ...this.product.variations] : [this.product];
    },
  },

  watch: {
    product: {
      handler(product) {
        if (this.product && this.selectedProduct) {
          const variationIds = this.product.variations ? this.product.variations.map((variation) => variation.id) : [this.product.id];
          if (this.product.id !== this.selectedProduct.id && !variationIds.filter((id) => id === this.selectedProduct.id).length) {
            this.imageLoaded = false;
            this.selectedProduct = product;
          }
        }
      },
    },
    selectedProduct: {
      handler(product) {
        if (product) {
          this.$emit('changeProduct', product);
        }
      },
    },
  },

  created() {
    this.swipe = new Swipe();
    this.swipe.setOnLeft(() => this.swipeLeft());
    this.swipe.setOnRight(() => this.swipeRight());
  },

  mounted() {
    this.swipe.attachListeners(this.$refs.productLink);
    if (this.viewdFn) {
      this.initInterObserver();
    }
  },

  beforeUnmount() {
    this.swipe.detachListeners(this.$refs.productLink);
  },

  methods: {
    onImageError() {
      this.imageError = true;
      this.imageLoaded = false;
    },

    onImageLoad() {
      this.imageError = false;
      this.imageLoaded = true;
    },

    hover(product) {
      if (product.isSkeleton) {
        return;
      }
      if (this.selectedProduct && this.selectedProduct.id !== product.id) {
        this.imageLoaded = false;
        this.selectedProduct = product;
      }
    },

    swipeLeft() {
      this.imageLoaded = false;
      const product = this.getNextVariation(this.selectedProduct);

      if (product) {
        this.selectedProduct = product;
      }
    },

    swipeRight() {
      this.imageLoaded = false;
      const product = this.getPreviousVariation(this.selectedProduct);

      if (product) {
        this.selectedProduct = product;
      }
    },

    getNextVariation(product) {
      const index = this.getVariationIndex(product);

      if (!this.variations.length || index < 0) {
        return null;
      }

      if (index + 1 > this.variations.length) {
        return this.variations[0];
      }

      return this.variations[index + 1];
    },

    getPreviousVariation(product) {
      const index = this.getVariationIndex(product);

      if (!this.variations.length || index < 0) {
        return null;
      }

      if (index - 1 < 0) {
        return this.variations[this.variations.length - 1];
      }

      return this.variations[index - 1];
    },

    getVariationIndex(product) {
      let index = -1;

      if (!product) {
        return null;
      }

      this.variations.find((variation, i) => {
        if (product.id === variation.id) {
          index = i;
          return true;
        }
        return false;
      });
      return index;
    },

    initInterObserver() {
      if ('IntersectionObserver' in window) {
        const options = {
          threshold: 1.0,
        };

        // eslint-disable-next-line no-shadow
        const observer = new IntersectionObserver((entries, observer) => {
          const [entry] = entries;
          if (parseInt(entry.intersectionRatio, 10) === 1) {
            this.viewdFn({
              productId: (this.selectedProduct && this.selectedProduct.id) || this.product.id,
            });
          }
        }, options);

        observer.observe(this.$el);
      } else {
        console.error('IntersectionObserver is not supported. Please check the browser version or if you have the correct polyfills');
      }
    },

    onFavoriteClicked() {
      const payload = {
        product: { ...this.selectedProduct },
        value: !this.favorite,
        target: this.favoriteButton,
      };
      this.updateFavoriteData(payload);
      this.$emit('favoriteClick', payload);
    },

    click() {
      if (this.displayedProduct.isSkeleton) {
        return;
      }
      this.$emit('click');
    },
  },
};
</script>
<style lang="postcss">
@import '@/pixel/style/variables';

.product-container {
  position: relative;

  &.space-bottom {
    margin-bottom: 40px;
  }

  &.hover {
    @media (min-width: $tablet-breakpoint) {
      .favorite {
        opacity: 0;
        transition: opacity 150ms ease-out, transform 200ms ease-out;
      }

      /* purgecss ignore */
      &:hover {
        .favorite {
          opacity: 1;
        }
      }
    }
  }

  .product-maintain-aspect {
    width: 100%;
    /* preserve aspect ratio of 289:150 */
    padding-bottom: 51.903%;
    position: relative;

    img {
      position: absolute;
    }
  }

  .product-image-container {
    position: relative;
    transition: opacity 100ms ease-out, transform 100ms ease-out;
    opacity: 1;

    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
      transition: opacity 200ms ease-out;
    }
  }

  .product-image-container:hover {
    transform: scale(1.05);
  }

  .product-image {
    opacity: 1;
    .image {
      &.skeleton {
        position: absolute;
        width: calc(100% - 20px);
        left: 10px;
        right: 10px;
        bottom: 10px;
        top: 10px;
        height: auto;
        img {
          opacity: 0;
        }
      }
    }
  }

  .product-link {
    position: relative;
    transition: opacity 100ms ease-out;
    opacity: 1;
    display: block;
    margin: 10px 10px 0px 10px;

    .badge {
      left: 12px;
    }
  }

  .product-title {
    margin-top: 5px;
    font-size: var(--font-size-14);
    line-height: var(--line-height-14);
    text-align: center;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  .product-detail {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 12px;
  }

  .prices {
    display: flex;
    line-height: 12px;
    text-align: right;
    color: var(--accent-color);
    margin-bottom: 10px;
    .originalPrice {
      margin-right: 8px;
      font-size: var(--font-size);
      text-decoration: line-through;
      color: var(--grey-medium);
    }
    .newPrice {
      font-family: var(--font-bold);
      font-size: var(--font-size);
    }
  }

  .product-variations {
    max-width: 200px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    .product-variation {
      line-height: 1em;
      margin-right: 1px;

      @media (max-width: $tablet-breakpoint) {
        a {
          position: relative;
          display: block;
          z-index: -1;
        }
      }

      /* purgecss ignore */
      &:hover {
        img {
          border: 3px solid var(--accent-color);
        }
      }

      &.active {
        a {
          z-index: 0;
          transition-delay: 0.1s;
        }
        img {
          border: 3px solid var(--white);
          box-shadow: 0 0 0 1px var(--coral);
        }
      }

      img {
        display: inline-block;
        width: 22px;
        height: 22px;
        border-radius: 15px;
        border: 3px solid var(--white);
        transition: border-color 100ms linear;
      }

      .image.skeleton {
        height: 22px;
        width: 22px;
        display: block;
        margin: 3px;
        border-radius: var(--circle-border-radius);
        img {
          opacity: 0;
        }
      }
    }
  }

  .favorite {
    position: absolute;
    padding: 8px;
    top: 0;
    right: 0;
    cursor: pointer;

    transition: transform 200ms ease-out;

    /* purgecss ignore */
    &:hover {
      transform: scale(1.2);
    }

    .af {
      font-size: 1.3em;
    }

    .af-heart-full {
      color: var(--accent-color);
    }
  }

  .hide-on-mobile {
    @media (max-width: calc($mobile-breakpoint - 1px)) {
      display: none;
    }
  }

  .product-detail-mobile {
    display: flex;
    justify-content: space-between;
    padding: 0px 20px;

    @media only screen and (max-width: 350px) {
      padding: 0px;
    }

    @media (min-width: $mobile-breakpoint) {
      display: none;
    }

    .product-title {
      margin-bottom: var(--space-2);
      text-align: left;
    }
    .slot-wrapper {
      align-self: center;
    }
  }

  &.skeleton-product {
    pointer-events: none;

    .skeleton {
      &.product-title {
        width: 50%;
        line-height: var(--font-size);
        margin-left: auto;
        margin-right: auto;
        color: transparent;
      }

      &.newPrice {
        line-height: var(--font-size);
      }
    }
  }
}
</style>
